/* eslint-disable @typescript-eslint/no-explicit-any */
import CryptoJS from "crypto-js";
import {additionalProfilesInitialState} from "store/additional-profiles/additionalProfilesReducers";
import {backgroundTaskReducerInitialState} from "store/background-tasks/backgroundTaskReducer";
import {bulkImportInitialState} from "store/bulk-import/bulkImportReducer";
import {candidateInitialState} from "store/candidate/candidateReducers";
import {companyInitialState} from "store/company/companyReducers";
import {eventsInitialState} from "store/events/eventTrackingReducers";
import {filterInitialState} from "store/filter/filterReducers";
import {layoutInitialState} from "store/layout/layoutReducers";
import {marketMapInitialState} from "store/market-map/marketMapReducers";
import {marketReportReducerInitialState} from "store/market-report/marketReportReducers";
import {orgInitialState} from "store/organisation/organisationReducers";
import {projectInitialState} from "store/project-list/projectListReducer";
import {mapxProjectInitialState} from "store/project/projectReducer";
import {refreshCandidateInitialState} from "store/refresh-candidate/refreshCandidatesReducer";
import {searchInitialState} from "store/search/searchReducers";
import {targetListInitialState} from "store/target-list/targetListReducers";
import {userInitialState} from "store/user/userReducers";
import {userPreferenceInitialState} from "store/userPreference/userPreferenceReducers";
import {reportBuilderInitialState} from "store/report-builder/reportBuilderSlice";
import {PersistedState, RootState} from "types/store.types";

// ============================================
// CONSTANTS
// ============================================

const STATE_KEY = "state";
const STATE_VERSION_KEY = "state_version";
const CURRENT_STATE_VERSION = 1;
const SECRET_KEY_STORAGE_KEY = "mapx_secure_uid";

/**
 * Gets or generates the encryption secret key
 * The key is stored in localStorage to ensure consistency across sessions
 */
const getSecretKey = (): string => {
	if (typeof window === "undefined" || !window.localStorage) {
		return "server-side-fallback-key";
	}

	try {
		let key = localStorage.getItem(SECRET_KEY_STORAGE_KEY);

		if (!key) {
			// Generate a new key
			const array = new Uint32Array(8);
			window.crypto.getRandomValues(array);
			key = Array.from(array, (dec) => dec.toString(16)).join("");
			localStorage.setItem(SECRET_KEY_STORAGE_KEY, key);
			console.info("🔑 Generated new encryption key");
		} else {
			console.debug("🔑 Retrieved existing encryption key");
		}

		return key;
	} catch (error) {
		console.error("❌ Error getting secret key:", error);

		return "fallback-encryption-key-static";
	}
};

// Initialize the secret key
// We no longer use a module-level constant to avoid staleness issues during key rotation
// Instead, functions call getSecretKey() directly when needed.

// ============================================
// THROTTLE UTILITY
// ============================================

/**
 * Throttles a function to execute at most once per specified time period
 * @param func - Function to throttle
 * @param wait - Minimum time between executions in milliseconds
 * @returns Throttled function
 */
function throttle<T extends (...args: any[]) => any>(
	func: T,
	wait: number,
): (...args: Parameters<T>) => void {
	let timeout: NodeJS.Timeout | null = null;
	let lastRan: number | null = null;

	return function executedFunction(...args: Parameters<T>): void {
		const now = Date.now();

		if (!lastRan) {
			func(...args);
			lastRan = now;
		} else {
			if (timeout) {
				clearTimeout(timeout);
			}

			const timeUntilNext = wait - (now - lastRan);

			timeout = setTimeout(
				() => {
					if (lastRan && Date.now() - lastRan >= wait) {
						func(...args);
						lastRan = Date.now();
					}
				},
				timeUntilNext > 0 ? timeUntilNext : 0,
			);
		}
	};
}

// ============================================
// STATE SHAPE VALIDATION
// ============================================

// Maps every persisted slice key to its reducer's initialState.
// When a persisted slice is loaded, it is deep-merged with the corresponding
// initialState so that any field added in a new release is automatically
// filled in — no hardcoded field names needed.
const SLICE_INITIAL_STATES: Record<keyof PersistedState, Record<string, unknown>> = {
	user: userInitialState as Record<string, unknown>,
	organisation: orgInitialState as Record<string, unknown>,
	search: searchInitialState as Record<string, unknown>,
	targetList: targetListInitialState as Record<string, unknown>,
	projectList: projectInitialState as Record<string, unknown>,
	candidate: candidateInitialState as unknown as Record<string, unknown>,
	bulkImport: bulkImportInitialState as Record<string, unknown>,
	backgroundTask: backgroundTaskReducerInitialState as Record<string, unknown>,
	refresh_candidate: refreshCandidateInitialState as Record<string, unknown>,
	company: companyInitialState as Record<string, unknown>,
	filter: filterInitialState as Record<string, unknown>,
	mapxProject: mapxProjectInitialState as unknown as Record<string, unknown>,
	events: eventsInitialState as Record<string, unknown>,
	additional_profiles: additionalProfilesInitialState as Record<string, unknown>,
	market_map: marketMapInitialState as Record<string, unknown>,
	marketReport: marketReportReducerInitialState as Record<string, unknown>,
	layout: layoutInitialState as Record<string, unknown>,
	userPreference: userPreferenceInitialState as Record<string, unknown>,
	reportBuilder: reportBuilderInitialState as unknown as Record<string, unknown>,
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
	return value !== null && typeof value === "object" && !Array.isArray(value);
}

// Recursively merges a persisted slice with its initialState.
// - Objects: merged so new keys from initialState are filled in.
// - Arrays / primitives: persisted value wins (user data is preserved).
// - Keys absent from initialState are dropped so removed fields don't survive across releases.
function deepMergeWithInitial(
	persisted: Record<string, unknown>,
	initial: Record<string, unknown>,
): Record<string, unknown> {
	const result: Record<string, unknown> = {...initial};

	for (const key of Object.keys(initial)) {
		const persistedValue = persisted[key];
		const initialValue = initial[key];

		if (persistedValue === undefined) {
			// Key missing in persisted state — keep the initialState value already in result
			continue;
		}

		if (isPlainObject(persistedValue) && isPlainObject(initialValue)) {
			result[key] = deepMergeWithInitial(persistedValue, initialValue);
		} else {
			result[key] = persistedValue;
		}
	}

	return result;
}

function validatePersistedState(state: Partial<RootState>): Partial<RootState> {
	const validated: Partial<RootState> = {};

	for (const key of Object.keys(SLICE_INITIAL_STATES) as (keyof PersistedState)[]) {
		const persisted = (state as Record<string, unknown>)[key];

		if (persisted === undefined || persisted === null) {
			continue;
		}

		if (!isPlainObject(persisted)) {
			console.warn(`State validation: dropping malformed slice "${key}"`);
			continue;
		}

		(validated as Record<string, unknown>)[key] = deepMergeWithInitial(
			persisted,
			SLICE_INITIAL_STATES[key],
		);
	}

	return validated;
}

// ============================================
// LOAD STATE FROM LOCALSTORAGE
// ============================================

/**
 * Loads the persisted state from localStorage
 * Handles errors gracefully and validates state version
 *
 * @returns Persisted state or undefined if not found/invalid
 */
export function loadState(): Partial<RootState> | undefined {
	try {
		// Check if localStorage is available
		if (typeof window === "undefined" || !window.localStorage) {
			console.warn("localStorage is not available");

			return undefined;
		}

		const serializedState = localStorage.getItem(STATE_KEY);
		const stateVersion = localStorage.getItem(STATE_VERSION_KEY);

		// No saved state
		if (serializedState === null) {
			console.info("No persisted state found");

			return undefined;
		}

		// Check version compatibility
		const version = stateVersion ? parseInt(stateVersion, 10) : 0;

		if (version < CURRENT_STATE_VERSION) {
			console.warn(
				`State version mismatch. Saved: ${version}, Current: ${CURRENT_STATE_VERSION}. Clearing old state.`,
			);
			localStorage.removeItem(STATE_KEY);
			localStorage.removeItem(STATE_VERSION_KEY);

			return undefined;
		}

		// Check if data is encrypted (CryptoJS AES encrypted data starts with "U2FsdGVkX1")
		const isEncrypted = serializedState.startsWith("U2FsdGVkX1");

		if (isEncrypted) {
			// Data is encrypted, decrypt it
			try {
				const currentKey = getSecretKey();
				// Ensure we have a valid secret key
				if (!currentKey || currentKey === "server-side-fallback-key") {
					console.error("❌ Invalid encryption key for decryption");

					throw new Error("Invalid encryption key");
				}

				const bytes = CryptoJS.AES.decrypt(serializedState, currentKey);
				const decryptedData = bytes.toString(CryptoJS.enc.Utf8);

				if (!decryptedData) {
					console.error("❌ Decryption returned empty data");

					throw new Error("Decryption failed - empty result");
				}

				const parsedState = JSON.parse(decryptedData) as Partial<RootState>;

				if (process.env?.NODE_ENV === "development") {
					console.info("✅ Encrypted state loaded from localStorage");
				}

				return validatePersistedState(parsedState);
			} catch (decryptionError) {
				console.error("❌ Failed to decrypt state:", decryptionError);

				// Check if this is a "Malformed UTF-8" error, which means the data was encrypted with a different key
				const errorMessage =
					decryptionError instanceof Error
						? decryptionError.message
						: String(decryptionError);
				const isMalformedError = errorMessage.includes("Malformed UTF-8");

				if (isMalformedError) {
					console.warn(
						"⚠️ Data was encrypted with a different key. Clearing encryption key and state for fresh start.",
					);
					// Clear the encryption key so a new one will be generated on next load
					localStorage.removeItem(SECRET_KEY_STORAGE_KEY);
				}

				// Clear corrupted encrypted state
				localStorage.removeItem(STATE_KEY);
				localStorage.removeItem(STATE_VERSION_KEY);
				console.info("🗑️ Cleared corrupted encrypted state. Please reload the page.");

				return undefined;
			}
		} else {
			// Data is not encrypted, try to parse as plain JSON (migration path)
			console.warn("⚠️ Found unencrypted state, attempting migration");
			try {
				const parsedState = JSON.parse(serializedState) as Partial<RootState>;
				console.info("✅ Legacy unencrypted state loaded from localStorage");
				// The next save will encrypt it

				return validatePersistedState(parsedState);
			} catch (jsonError) {
				console.error("❌ Failed to parse unencrypted state as JSON:", jsonError);
				// Clear corrupted state
				localStorage.removeItem(STATE_KEY);
				localStorage.removeItem(STATE_VERSION_KEY);
				console.info("🗑️ Cleared corrupted unencrypted state");

				return undefined;
			}
		}
	} catch (err) {
		console.error("❌ Error loading state from localStorage:", err);

		// Try to recover by clearing corrupted state
		try {
			localStorage.removeItem(STATE_KEY);
			localStorage.removeItem(STATE_VERSION_KEY);
			console.info("🗑️ Cleared state due to error");
		} catch (clearError) {
			console.error("❌ Failed to clear corrupted state:", clearError);
		}

		return undefined;
	}
}

// ============================================
// SAVE STATE TO LOCALSTORAGE (INTERNAL)
// ============================================

/**
 * Internal function to save state to localStorage
 * Should not be called directly - use the throttled version
 *
 * @param state - State to persist
 */
function saveStateInternal(state: Partial<PersistedState>): void {
	try {
		// Check if localStorage is available
		if (typeof window === "undefined" || !window.localStorage) {
			console.warn("localStorage is not available");

			return;
		}

		// Serialize state
		const serializedState = JSON.stringify(state);

		// Encrypt state
		const currentKey = getSecretKey();
		const encryptedState = CryptoJS.AES.encrypt(serializedState, currentKey).toString();

		// Check size (warn if > 5MB, localStorage limit is usually 5-10MB)
		const sizeInMB = new Blob([encryptedState]).size / (1024 * 1024);
		if (sizeInMB > 5) {
			console.warn(
				`⚠️ Large state size: ${sizeInMB.toFixed(2)}MB. Consider persisting less data.`,
			);
		}

		// Save to localStorage
		localStorage.setItem(STATE_KEY, encryptedState);
		localStorage.setItem(STATE_VERSION_KEY, CURRENT_STATE_VERSION.toString());

		// Log in development only
		if (process.env.NODE_ENV === "development") {
			console.debug(`💾 Encrypted state saved to localStorage (${sizeInMB.toFixed(2)}MB)`);
		}
	} catch (err) {
		console.error("❌ Error saving state to localStorage:", err);

		// Handle QuotaExceededError
		if (err instanceof DOMException && err.name === "QuotaExceededError") {
			console.error(
				"localStorage quota exceeded. Consider clearing old data or persisting less state.",
			);

			// Try to recover by clearing state
			try {
				localStorage.removeItem(STATE_KEY);
				console.info("Cleared state due to quota error");
			} catch (clearError) {
				console.error("Failed to clear state:", clearError);
			}
		}
	}
}

// ============================================
// SAVE STATE TO LOCALSTORAGE (THROTTLED)
// ============================================

/**
 * Throttled version of saveState
 * Saves at most once per 250ms to prevent performance issues
 *
 * Usage:
 * ```typescript
 * store.subscribe(() => {
 *   saveState(transformedState);
 * });
 * ```
 */
export const saveState = throttle(saveStateInternal, 250);

// ============================================
// CLEAR STATE FROM LOCALSTORAGE
// ============================================

/**
 * Clears all persisted state from localStorage
 * Useful for logout or reset functionality
 */
export function clearState(): void {
	try {
		if (typeof window === "undefined" || !window.localStorage) {
			console.warn("localStorage is not available");

			return;
		}

		localStorage.removeItem(STATE_KEY);
		localStorage.removeItem(STATE_VERSION_KEY);
		console.info("✅ State cleared from localStorage");
	} catch (err) {
		console.error("❌ Error clearing state from localStorage:", err);
	}
}

// ============================================
// GET STATE SIZE (UTILITY)
// ============================================

/**
 * Gets the size of the persisted state in localStorage
 * @returns Size in megabytes
 */
export function getStateSize(): number {
	try {
		if (typeof window === "undefined" || !window.localStorage) {
			return 0;
		}

		const serializedState = localStorage.getItem(STATE_KEY);

		if (!serializedState) {
			return 0;
		}

		const sizeInBytes = new Blob([serializedState]).size;

		return sizeInBytes / (1024 * 1024); // Convert to MB
	} catch (err) {
		console.error("Error calculating state size:", err);

		return 0;
	}
}

// ============================================
// EXPORTS
// ============================================

export default {
	loadState,
	saveState,
	clearState,
	getStateSize,
	setItem,
	getItem,
	removeItem,
};

// ============================================
// GENERIC STORAGE HELPERS
// ============================================

/**
 * Set an item in localStorage with encryption
 * @param key - Storage key
 * @param value - Value to store
 */
export function setItem(key: string, value: string): void {
	try {
		if (typeof window === "undefined" || !window.localStorage) {
			return;
		}

		const currentKey = getSecretKey();
		const encryptedValue = CryptoJS.AES.encrypt(value, currentKey).toString();
		localStorage.setItem(key, encryptedValue);
	} catch (err) {
		console.error(`❌ Error saving ${key} to localStorage:`, err);
	}
}

/**
 * Get an item from localStorage with decryption
 * @param key - Storage key
 * @returns Decrypted value or null
 */
export function getItem(key: string): string | null {
	try {
		if (typeof window === "undefined" || !window.localStorage) {
			return null;
		}

		const value = localStorage.getItem(key);

		if (value === null) {
			return null;
		}

		// An empty string is a valid value and should not be decrypted.
		if (value === "") {
			return "";
		}

		// Check if the value is encrypted using the OpenSSL-compatible format used by CryptoJS.
		// "U2FsdGVkX1" is the Base64 encoding of "Salted__", which is the default header.
		if (!value.startsWith("U2FsdGVkX1")) {
			return value;
		}

		try {
			const currentKey = getSecretKey();
			const bytes = CryptoJS.AES.decrypt(value, currentKey);
			const decryptedData = bytes.toString(CryptoJS.enc.Utf8);

			if (!decryptedData) {
				return null;
			}

			return decryptedData;
		} catch (e) {
			// If decryption fails, it means the key is likely different or data is corrupted.
			// Return null as requested data cannot be safely retrieved.
			console.warn(`⚠️ Failed to decrypt ${key} from localStorage`);

			return null;
		}
	} catch (err) {
		console.error(`❌ Error reading ${key} from localStorage:`, err);

		return null;
	}
}

/**
 * Remove an item from localStorage
 * @param key - Storage key
 */
export function removeItem(key: string): void {
	try {
		if (typeof window === "undefined" || !window.localStorage) {
			return;
		}

		localStorage.removeItem(key);
	} catch (err) {
		console.error(`❌ Error removing ${key} from localStorage:`, err);
	}
}
